/**
 * Storage Metrics Component
 *
 * Displays storage capacity with:
 * - Pie chart showing used/free space
 * - Bar chart showing per-datastore capacity
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import EChart from '@/components/EchartCustomized/MapEchart'
import { SCHEMES } from '@/components/EchartCustomized'
import type { StorageCapacity, DatastoreCapacity } from '../../types'

interface StorageMetricsProps {
    storage: StorageCapacity
}

/** Format GB value for display */
const formatGB = (gb: number): string => {
    if (gb >= 1024) {
        return `${(gb / 1024).toFixed(1)} TB`
    }
    return `${gb.toFixed(0)} GB`
}

/** Create pie chart for used/free storage */
const createStoragePieChart = (storage: StorageCapacity) => {
    return {
        tooltip: {
            trigger: 'item',
            formatter: (params: any) =>
                `${params.name}: ${formatGB(params.value)}`,
        },
        legend: {
            bottom: 0,
            itemWidth: 10,
            itemHeight: 10,
        },
        series: [
            {
                type: 'pie',
                radius: ['45%', '70%'],
                avoidLabelOverlap: false,
                label: {
                    show: true,
                    position: 'center',
                    formatter: () => `${storage.usage_pct.toFixed(0)}%\nUsed`,
                    fontSize: 16,
                    fontWeight: 'bold',
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 18,
                    },
                },
                data: [
                    {
                        value: storage.used_gb,
                        name: 'Used',
                        itemStyle: { color: SCHEMES.warning[0] },
                    },
                    {
                        value: storage.free_gb,
                        name: 'Free',
                        itemStyle: { color: SCHEMES.success[0] },
                    },
                ],
            },
        ],
    }
}

/** Create bar chart for per-datastore capacity */
const createDatastoreBarChart = (datastores: DatastoreCapacity[]) => {
    // Limit to top 10 by total capacity
    const sortedDatastores = [...datastores]
        .sort((a, b) => b.capacity_gb - a.capacity_gb)
        .slice(0, 10)

    const names = sortedDatastores.map((d) => d.name)
    const usedData = sortedDatastores.map((d) => d.used_gb)
    const freeData = sortedDatastores.map((d) => d.free_gb)

    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params: any) => {
                const used = params[0]
                const free = params[1]
                const total = used.value + free.value
                return `${used.name}<br/>Used: ${formatGB(used.value)}<br/>Free: ${formatGB(free.value)}<br/>Total: ${formatGB(total)}`
            },
        },
        legend: {
            bottom: 0,
            data: ['Used', 'Free'],
            itemWidth: 10,
            itemHeight: 10,
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '15%',
            top: '3%',
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            data: names,
            axisLabel: {
                rotate: 30,
                interval: 0,
                width: 80,
                overflow: 'truncate',
            },
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                formatter: (value: number) => formatGB(value),
            },
        },
        series: [
            {
                name: 'Used',
                type: 'bar',
                stack: 'total',
                data: usedData,
                itemStyle: { color: SCHEMES.warning[0] },
            },
            {
                name: 'Free',
                type: 'bar',
                stack: 'total',
                data: freeData,
                itemStyle: { color: SCHEMES.success[0] },
            },
        ],
    }
}

const StorageMetrics = ({ storage }: StorageMetricsProps) => {
    const datastores = storage.datastores || []

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
            <Card className="lg:col-span-4 col-span-12">
                <CardHeader>
                    <CardTitle>Storage Overview</CardTitle>
                </CardHeader>
                <CardContent>
                    <EChart
                        options={createStoragePieChart(storage)}
                        style={{ height: 200 }}
                    />
                    <div className="text-center mt-2 text-sm text-muted-foreground">
                        {formatGB(storage.used_gb)} /{' '}
                        {formatGB(storage.total_capacity_gb)}
                    </div>
                </CardContent>
            </Card>

            <Card className="lg:col-span-8 col-span-12">
                <CardHeader>
                    <CardTitle>Storage by Datastore</CardTitle>
                </CardHeader>
                <CardContent>
                    {datastores.length > 0 ? (
                        <EChart
                            options={createDatastoreBarChart(datastores)}
                            style={{ height: 200 }}
                        />
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            No datastore details available
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

export default StorageMetrics
